import { inject, Injectable, signal } from "@angular/core";
import { isOfType } from "@bitwarden/web-vault/app/models/types/general-types";
import {
  AvailablePaymentCycles,
  PlanOption,
  PlanOptions,
  MONTH,
  YEAR,
  SubscriptionPaymentCycle,
} from "@bitwarden/web-vault/app/models/types/subscription.type";
import { StripeConfigService } from "@bitwarden/web-vault/app/services/api/stripe-config.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { Router } from "@angular/router";
import { ValidationService } from "@bitwarden/common/abstractions/validation.service";
import { SubscriptionHttpPollingService } from "@bitwarden/web-vault/app/services/subscription/subscription-http-polling.service";

@Injectable({
  providedIn: "root",
}) // todo consolidate wth context-menu.ts
export class PaymentService {
  time: string = "";
  stripeButtonAvailable = true;
  readonly YEAR = YEAR;
  readonly MONTH = MONTH;
  selectedPlan = signal(PlanOptions.premiumAnnual);
  protected userStoreService = inject(UserStoreService);
  protected loading = false;
  protected member: Record<string, any> = {};
  protected router: Router = inject(Router);
  protected stripeConfigService = inject(StripeConfigService);
  protected appState: AppStateStoreService = inject(AppStateStoreService);
  private validationService: ValidationService = inject(ValidationService);
  private subscriptionService: SubscriptionHttpPollingService = inject(
    SubscriptionHttpPollingService,
  );
  constructor() {}

  selectPlan(value: string) {
    if (isOfType<SubscriptionPaymentCycle>(value, AvailablePaymentCycles)) {
      if (value === MONTH) {
        this.selectedPlan.set(PlanOptions.premiumMonthly);
      } else if (value === YEAR) {
        this.selectedPlan.set(PlanOptions.premiumAnnual);
      }
    } else {
      throw new Error("Invalid plan selected");
    }
  }

  async confirmSubscription() {
    await this.handlePlanSubmit();
  }

  async handlePlanSubmit(withCard: boolean = false) {
    this.appState.startProcess("general", "");
    this.stripeButtonAvailable = false;
    try {
      await this.handleStripePlanSubmit(this.selectedPlan(), withCard);
    } finally {
      this.stripeButtonAvailable = true;
      this.appState.endProcess("general");
    }
  }

  async handleStripePlanSubmit(plan: PlanOption, withCard: boolean = false) {
    const stripeMapping = {
      [PlanOptions.premiumMonthly.label]: this.stripeConfigService.price.monthly,
      [PlanOptions.premiumAnnual.label]: this.stripeConfigService.price.annual,
    };

    const payload = this.stripeConfigService.setPayload(stripeMapping[plan.label], withCard);
    const response = await this.stripeConfigService.createCheckoutSessions(payload);

    if (response.url) {
      window.location = response.url;
    }
  }

  // Notifies (so the user gets immediate feedback), then rethrows: this is only called from
  // Bootstrap's first-login branch, which has its own catch around this call that reports the
  // failure as an APM signal (as of #4199) without touching firstLoginSetupSucceeded — a trial
  // failure must not gate the guide wizard/dialog, since isFirstEverLogin() has already flipped
  // false by the time this runs. Swallowing the error here used to strand a failed trial
  // activation with no caller-side signal at all.
  async autoGetFreeTrial(cycle: "annual" | "monthly" = "monthly") {
    try {
      const response = await this.stripeConfigService.triggerFreeTrial(
        this.stripeConfigService.price[cycle],
      );
      await this.subscriptionService.pollSubStatus(response.subscriptionId, true);
    } catch (error) {
      // showError translates the title and reads the error's message, keeping the network-failure
      // message for offline users; passing the raw error as toast text showed the untranslated key
      // and "[object Object]" (#6268).
      this.validationService.showError(error);
      throw error;
    }
  }
}
